import { FC } from 'react'

import { Container, Modal, PrimaryButton } from '~/components'
import {
  ButtonIconWithLabel,
  LinkToMap,
  SearchResultCount,
} from '~/features/common'

import { useSearchFormContext, useSearchHitCount } from './hooks'
import {
  FormValues,
  SearchForm,
  SearchFormContext,
  FormProvider,
} from './search-form'

type SearchFormModalProps = {
  isOpen: boolean
  animation: 'appear' | 'disappear' | undefined
  closeModal: () => void
  searchFormContext: SearchFormContext
}

export const SearchFormModal: FC<SearchFormModalProps> = ({
  isOpen,
  animation,
  closeModal,
  searchFormContext,
}) => {
  const { searchHitCount } = useSearchHitCount(
    searchFormContext.watchedFormValues,
  )

  return (
    <Modal
      animation={animation}
      closeModal={closeModal}
      isOpen={isOpen}
      modalHeightClassName="h-full"
      modalWidthClassName="w-full"
    >
      <div>
        <div className="fixed inset-x-0 z-[10001] flex h-[58px] border-b border-grayBorder bg-white">
          <div className="relative flex w-full items-center justify-center">
            <h1 className="">検索条件</h1>
            <div className="absolute right-4">
              <ButtonIconWithLabel buttonType="close" onClick={closeModal} />
            </div>
          </div>
        </div>
        <div className="pb-[90px] pt-[60px]">
          <SearchForm {...searchFormContext}>
            <div className="fixed inset-x-0 bottom-0 border-t border-grayBorder bg-white">
              <Container>
                <div className="flex w-full justify-center gap-[5%] py-[10px]">
                  <SearchResultCount
                    count={searchHitCount}
                    layout="column"
                    preCountText="ヒット件数"
                  />
                  <PrimaryButton
                    className="block w-[220px] min-w-0"
                    color="brand"
                  >
                    検索結果を表示
                  </PrimaryButton>
                </div>
              </Container>
            </div>
          </SearchForm>
        </div>
      </div>
    </Modal>
  )
}

type FromAreaProps = {
  areaFormOptions: { label: string; value: string }[]
  facilitiesAndServicesFormOptions: { label: string; facilityId: string }[]
  officeTypeFormOptions: { label: string; value: string }[]
  animation: 'appear' | 'disappear' | undefined
  isOpen: boolean
  initialValues: FormValues
  mapLink: string
  closeModal: () => void
  onSubmit?: () => void
}

export const SearchFormArea: FC<FromAreaProps> = ({
  areaFormOptions,
  officeTypeFormOptions,
  facilitiesAndServicesFormOptions,
  animation,
  isOpen,
  closeModal,
  onSubmit,
  initialValues,
  mapLink,
}) => {
  const formContext = useSearchFormContext(
    initialValues,
    areaFormOptions,
    officeTypeFormOptions,
    facilitiesAndServicesFormOptions,
    onSubmit,
  )

  return (
    <FormProvider context={formContext.form.context}>
      <div className="hidden shrink-0 md:block">
        <div>
          <div className="text-lg font-bold leading-[140%]">地図で探す</div>
          <div
            className="mt-4"
            style={{ backgroundImage: `url('/images/map2.png')` }}
          >
            <LinkToMap
              className="h-[173px]"
              href={mapLink}
              iconIdSuffix="pc-map-above-form"
              variant="button"
            />
          </div>
        </div>
        <div className="mt-10">
          <div className="text-lg font-bold leading-[140%]">検索条件</div>
          <div className="mt-4 rounded border border-grayBorder">
            <SearchForm {...formContext}>
              <div className="flex justify-center border-t border-grayBorder p-4">
                <PrimaryButton className="block min-w-0" color="brand">
                  検索結果を表示
                </PrimaryButton>
              </div>
            </SearchForm>
          </div>
        </div>
      </div>
      <SearchFormModal
        animation={animation}
        closeModal={closeModal}
        isOpen={isOpen}
        searchFormContext={formContext}
      />
    </FormProvider>
  )
}
